/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * API Client Utilities for LUKHAS Services
 *
 * Provides typed clients for:
 * - Terminal Service (port 8085)
 * - Editor Service (port 8000)
 * - NIΛS Service (port 8086)
 * - DΛST Service (port 8087)
 */

// === Configuration ===

const API_BASE_URLS = {
  terminal: import.meta.env.VITE_TERMINAL_API_URL || 'http://localhost:8085',
  editor: import.meta.env.VITE_EDITOR_API_URL || 'http://localhost:8000',
  nias: import.meta.env.VITE_NIAS_API_URL || 'http://localhost:8086',
  dast: import.meta.env.VITE_DAST_API_URL || 'http://localhost:8087',
}

// === Type Definitions ===

// Terminal Types
export interface TerminalSession {
  session_id: string
  mode: 'local' | 'sandbox' | 'bastion'
  created_at: string
  status: 'active' | 'inactive'
}

export interface TerminalExecRequest {
  session_id: string
  command: string
}

export interface TerminalExecResponse {
  stdout: string
  stderr: string
  exit_code: number
  execution_time: number
}

// Editor Types
export interface Draft {
  id: string
  title: string
  markdown: string
  json_ast: any
  tenant_id: string
  user_id: string
  created_at: string
  updated_at: string
}

export interface DraftVersion {
  version_id: number
  draft_id: string
  markdown: string
  json_ast: any
  created_at: string
  created_by: string
}

// NIΛS Types
export interface SymbolicMessage {
  id: string
  content: string
  tags: string[]
  tier: 'PUBLIC' | 'PERSONAL' | 'CREATIVE' | 'ENTERPRISE' | 'QUANTUM'
  emotional_tone: 'CALM' | 'FOCUSED' | 'CREATIVE' | 'STRESSED' | 'OVERWHELMED' | 'DREAMING'
  intensity: number
  dream_based: boolean
  lambda_signature: string
  image_url?: string
  expires_at?: string
}

export interface DeliveryResult {
  status: 'delivered' | 'blocked' | 'deferred' | 'transformed'
  message_id: string
  reason: string
  delivery_method?: string
  scheduled_for?: string
  lambda_trace?: string
  timestamp: string
}

// DΛST Types
export interface SymbolicTag {
  symbol: string
  category: 'ACTIVITY' | 'CONTEXT' | 'MOOD' | 'FOCUS' | 'GOAL' | 'RELATIONSHIP' | 'TEMPORAL' | 'CREATIVE' | 'LEARNING' | 'WELLNESS'
  source: 'USER_EXPLICIT' | 'CALENDAR' | 'ACTIVITY_TRACKER' | 'LOCATION' | 'BIOMETRIC' | 'APP_INTEGRATION' | 'AI_INFERENCE' | 'SOCIAL' | 'ENVIRONMENTAL'
  confidence: number
  weight: number
  lambda_signature: string
  is_valid: boolean
  created_at: string
  last_updated: string
  expires_at?: string
  metadata?: Record<string, any>
}

export interface ContextSnapshot {
  user_id: string
  tags: SymbolicTag[]
  primary_activity?: string
  focus_score: number
  coherence_score: number
  stability_score: number
  lambda_fingerprint: string
  timestamp: string
}

// === Utility Functions ===

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }

  return response.json()
}

// === Terminal API Client ===

export const TerminalAPI = {
  /**
   * Create a new terminal session
   */
  async createSession(mode: 'local' | 'sandbox' | 'bastion' = 'local'): Promise<TerminalSession> {
    return fetchJSON<TerminalSession>(`${API_BASE_URLS.terminal}/api/terminal/session`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    })
  },

  /**
   * Execute a command in a terminal session
   */
  async exec(session_id: string, command: string): Promise<TerminalExecResponse> {
    return fetchJSON<TerminalExecResponse>(`${API_BASE_URLS.terminal}/api/terminal/exec`, {
      method: 'POST',
      body: JSON.stringify({ session_id, command }),
    })
  },

  /**
   * Create SSE stream for real-time terminal output
   */
  createStream(session_id: string, command: string): EventSource {
    const url = new URL(`${API_BASE_URLS.terminal}/api/terminal/exec-stream`)
    url.searchParams.set('session_id', session_id)
    url.searchParams.set('command', command)
    return new EventSource(url.toString())
  },

  /**
   * Close a terminal session
   */
  async closeSession(session_id: string): Promise<void> {
    await fetchJSON(`${API_BASE_URLS.terminal}/api/terminal/session/${session_id}`, {
      method: 'DELETE',
    })
  },

  /**
   * Get command history for a session
   */
  async getHistory(session_id: string): Promise<{ history: string[] }> {
    return fetchJSON(`${API_BASE_URLS.terminal}/api/terminal/history/${session_id}`)
  },
}

// === Editor API Client ===

export const EditorAPI = {
  /**
   * Create or update a draft
   */
  async saveDraft(draft: Partial<Draft>): Promise<Draft> {
    return fetchJSON<Draft>(`${API_BASE_URLS.editor}/api/editor/draft`, {
      method: 'POST',
      body: JSON.stringify(draft),
    })
  },

  /**
   * Get a draft by ID
   */
  async getDraft(id: string): Promise<Draft> {
    return fetchJSON<Draft>(`${API_BASE_URLS.editor}/api/editor/draft/${id}`)
  },

  /**
   * List all drafts for current user
   */
  async listDrafts(): Promise<{ drafts: Draft[] }> {
    return fetchJSON(`${API_BASE_URLS.editor}/api/editor/drafts`)
  },

  /**
   * Get version history for a draft
   */
  async getHistory(draft_id: string): Promise<{ versions: DraftVersion[] }> {
    return fetchJSON(`${API_BASE_URLS.editor}/api/editor/draft/${draft_id}/history`)
  },

  /**
   * Revert to a specific version
   */
  async revertToVersion(draft_id: string, version_id: number): Promise<Draft> {
    return fetchJSON<Draft>(`${API_BASE_URLS.editor}/api/editor/draft/${draft_id}/revert`, {
      method: 'POST',
      body: JSON.stringify({ version_id }),
    })
  },

  /**
   * Export draft (md, json, or zip)
   */
  async exportDraft(draft_id: string, format: 'md' | 'json' | 'zip'): Promise<Blob> {
    const response = await fetch(`${API_BASE_URLS.editor}/api/editor/export/${draft_id}?format=${format}`)
    if (!response.ok) throw new Error(`Export failed: ${response.statusText}`)
    return response.blob()
  },

  /**
   * Publish draft to GitHub as PR
   */
  async publishToGitHub(draft_id: string, repo: string, branch: string, pr_title: string): Promise<{ pr_url: string }> {
    return fetchJSON(`${API_BASE_URLS.editor}/api/editor/publish`, {
      method: 'POST',
      body: JSON.stringify({ draft_id, repo, branch, pr_title }),
    })
  },
}

// === NIΛS API Client ===

export const NIASAPI = {
  /**
   * Register a new user with NIΛS
   */
  async registerUser(user_id: string, tier: string, consent_level: string): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/register`, {
      method: 'POST',
      body: JSON.stringify({ user_id, tier, consent_level }),
    })
  },

  /**
   * Update user consent level
   */
  async updateConsent(user_id: string, consent_level: string): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/consent`, {
      method: 'POST',
      body: JSON.stringify({ user_id, consent_level }),
    })
  },

  /**
   * Update emotional state
   */
  async updateEmotionalState(user_id: string, emotional_vector: Record<string, number>): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/emotional-state`, {
      method: 'POST',
      body: JSON.stringify({ user_id, emotional_vector }),
    })
  },

  /**
   * Deliver a symbolic message
   */
  async deliverMessage(message: Partial<SymbolicMessage> & { user_id: string }): Promise<DeliveryResult> {
    return fetchJSON<DeliveryResult>(`${API_BASE_URLS.nias}/api/nias/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    })
  },

  /**
   * Create SSE stream for real-time dream commerce messages
   */
  createMessageStream(user_id: string): EventSource {
    return new EventSource(`${API_BASE_URLS.nias}/api/nias/messages/stream/${user_id}`)
  },

  /**
   * Process queued dream deliveries
   */
  async processDreamSession(user_id: string): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/dream-session`, {
      method: 'POST',
      body: JSON.stringify({ user_id }),
    })
  },

  /**
   * Get user status
   */
  async getUserStatus(user_id: string): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/status/${user_id}`)
  },

  /**
   * Get system analytics
   */
  async getAnalytics(): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.nias}/api/nias/analytics`)
  },
}

// === DΛST API Client ===

export const DASTAPI = {
  /**
   * Register a new user with DΛST
   */
  async registerUser(user_id: string, initial_tags?: string[]): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/register`, {
      method: 'POST',
      body: JSON.stringify({ user_id, initial_tags }),
    })
  },

  /**
   * Add a symbolic tag
   */
  async addSymbol(user_id: string, symbol: string, category: string, options?: {
    source?: string
    confidence?: number
    metadata?: Record<string, any>
    expires_in_hours?: number
  }): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/symbols/add`, {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        symbol,
        category,
        source: options?.source || 'USER_EXPLICIT',
        confidence: options?.confidence || 1.0,
        metadata: options?.metadata,
        expires_in_hours: options?.expires_in_hours,
      }),
    })
  },

  /**
   * Remove a symbolic tag
   */
  async removeSymbol(user_id: string, symbol: string, category?: string): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/symbols/remove`, {
      method: 'POST',
      body: JSON.stringify({ user_id, symbol, category }),
    })
  },

  /**
   * Query current symbols
   */
  async querySymbols(user_id: string, options?: {
    category_filter?: string
    confidence_threshold?: number
  }): Promise<{ symbols: string[] }> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/symbols/query`, {
      method: 'POST',
      body: JSON.stringify({
        user_id,
        category_filter: options?.category_filter,
        confidence_threshold: options?.confidence_threshold,
      }),
    })
  },

  /**
   * Get context snapshot
   */
  async getContextSnapshot(user_id: string): Promise<ContextSnapshot> {
    return fetchJSON<ContextSnapshot>(`${API_BASE_URLS.dast}/api/dast/context/${user_id}`)
  },

  /**
   * Create SSE stream for real-time context updates
   */
  createContextStream(user_id: string): EventSource {
    return new EventSource(`${API_BASE_URLS.dast}/api/dast/context/${user_id}/stream`)
  },

  /**
   * Integrate calendar events
   */
  async integrateCalendar(user_id: string, calendar_events: any[]): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/integration/calendar`, {
      method: 'POST',
      body: JSON.stringify({ user_id, calendar_events }),
    })
  },

  /**
   * Integrate activity data
   */
  async integrateActivity(user_id: string, activity_data: Record<string, any>): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/integration/activity`, {
      method: 'POST',
      body: JSON.stringify({ user_id, activity_data }),
    })
  },

  /**
   * Get available categories
   */
  async getCategories(): Promise<{ categories: Record<string, string> }> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/categories`)
  },

  /**
   * Get available sources
   */
  async getSources(): Promise<{ sources: Record<string, string> }> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/sources`)
  },

  /**
   * Get system analytics
   */
  async getAnalytics(): Promise<any> {
    return fetchJSON(`${API_BASE_URLS.dast}/api/dast/analytics`)
  },
}

// === Health Check Utilities ===

export async function checkServiceHealth(service: keyof typeof API_BASE_URLS): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URLS[service]}/health`)
    return response.ok
  } catch {
    return false
  }
}

export async function checkAllServices(): Promise<Record<string, boolean>> {
  const checks = await Promise.all(
    Object.keys(API_BASE_URLS).map(async (service) => ({
      service,
      healthy: await checkServiceHealth(service as keyof typeof API_BASE_URLS),
    }))
  )

  return Object.fromEntries(checks.map(({ service, healthy }) => [service, healthy]))
}
